import { useMemo, useState } from "react";
import {
    ArrowUp,
    Building2,
    Dumbbell,
    GraduationCap,
    Hammer,
    HeartPulse,
    Home,
    Landmark,
    Scissors,
    ShoppingBag,
    Sparkles,
    Utensils,
    Wand2
} from "lucide-react";
import {
    inferIndustryFromPrompt,
    getIndustryDefaults
} from "./generatorIndustryConfig";

const quickStarts = [
    {
        key: "sports-academy",
        label: "Sports Academy",
        industry: "Education",
        prompt: "Build a sports academy website for students and parents with programs, coaches, admissions, training outcomes, and contact enquiries.",
        icon: GraduationCap
    },
    {
        key: "fitness",
        label: "Fitness Studio",
        industry: "Fitness",
        prompt: "Build a fitness studio website with modern equipment, training programs, trainers, memberships, trial session CTA, and contact enquiries.",
        icon: Dumbbell
    },
    {
        key: "real-estate",
        label: "Real Estate",
        industry: "Real Estate",
        prompt: "Build a real estate website for property buyers with listings, services, local expertise, consultation CTA, and lead capture.",
        icon: Home
    },
    {
        key: "clinic",
        label: "Clinic",
        industry: "Healthcare",
        prompt: "Build a clinic website with services, doctors, appointments, trust signals, patient-friendly sections, and contact form.",
        icon: HeartPulse
    },
    {
        key: "store",
        label: "Online Store",
        industry: "Ecommerce / Store",
        prompt: "Build an ecommerce website with products, collections, offers, trust sections, and shopping-focused layout.",
        icon: ShoppingBag
    },
    {
        key: "restaurant",
        label: "Restaurant",
        industry: "Restaurant",
        prompt: "Build a restaurant website with menu, offers, gallery, table booking, location details, and order enquiries.",
        icon: Utensils
    },
    {
        key: "law-firm",
        label: "Law Firm",
        industry: "Legal",
        prompt: "Build a law firm website with practice areas, team, consultation CTA, credibility, and authority-focused sections.",
        icon: Landmark
    },
    {
        key: "beauty",
        label: "Beauty",
        industry: "Beauty",
        prompt: "Build a beauty studio website with services, packages, gallery, appointment CTA, and customer reviews.",
        icon: Scissors
    },
    {
        key: "construction",
        label: "Construction",
        industry: "Construction",
        prompt: "Build a construction company website with services, project portfolio, consultation CTA, trust signals, and contact enquiries.",
        icon: Hammer
    },
    {
        key: "saas",
        label: "SaaS",
        industry: "SaaS / Tech",
        prompt: "Build a SaaS website with features, pricing, conversion-focused sections, onboarding CTA, and product benefits.",
        icon: Building2
    }
];

function applyIndustryDefaults(updateField, industry) {
    const defaults = getIndustryDefaults(industry);

    updateField("industry", industry);
    updateField("subIndustry", defaults.subIndustry);
    updateField("businessModel", defaults.businessModel);
    updateField("primaryWebsiteMode", defaults.primaryWebsiteMode);
    updateField("primaryEntityType", defaults.primaryEntityType);
    updateField("locationFocus", defaults.locationFocus);
    updateField("pages", defaults.pages);
    updateField("callToActionType", defaults.ctas?.[0] || "Contact Us");
}

export default function MobileGeneratorComposer({
    form,
    updateField,
    onStart
}) {
    const [prompt, setPrompt] = useState("");

    const canSubmit =
        form.businessName?.trim()?.length > 1 &&
        prompt.trim().length >= 8;

    const selectedPreview = useMemo(() => {
        const inferred = inferIndustryFromPrompt(prompt);
        return inferred.industry || "AI will ask you to choose";
    }, [prompt]);

    const applyPrompt = (inputPrompt) => {
        const finalPrompt = String(inputPrompt || prompt || "").trim();
        if (!finalPrompt) return;

        const inferred = inferIndustryFromPrompt(finalPrompt);
        
        if (inferred.industry) {
            applyIndustryDefaults(updateField, inferred.industry);
            updateField("goalOfWebsite", inferred.goalOfWebsite || "Generate Leads");
            updateField("primaryAudience", inferred.primaryAudience || "Local customers");
        } else {
            updateField("goalOfWebsite", form.goalOfWebsite || "Generate Leads");
            updateField("primaryAudience", form.primaryAudience || "Local customers");
        }

        updateField("description", finalPrompt);
        updateField("brandStyle", form.brandStyle || "Premium");
        updateField("tone", form.tone || "Professional");

        if (onStart) onStart();
    };

    const applyQuickStart = (item) => {
        const defaults = getIndustryDefaults(item.industry);

        setPrompt(item.prompt);

        updateField("description", item.prompt);
        updateField("goalOfWebsite", defaults.ctas?.[0] || "Generate Leads");
        updateField("primaryAudience", defaults.audiences?.[0] || "Local customers");
        updateField("brandStyle", "Premium");
        updateField("tone", "Professional");

        applyIndustryDefaults(updateField, item.industry);

        if (!form.businessName?.trim()) {
            updateField("businessName", item.label);
        }

        if (onStart) onStart();
    };

    return (
        <section className="mgc-shell">
            <div className="mgc-topbar">
                <div className="mgc-brand-mark">
                    <Sparkles size={19} />
                </div>

                <div className="mgc-brand-copy">
                    <strong>Shriansh Nexus</strong>
                    <span>AI Website + CRM Builder</span>
                </div>

                <div className="mgc-status-pill">Beta</div>
            </div>

            <div className="mgc-hero">
                <div className="mgc-kicker">
                    <Wand2 size={14} />
                    <span>Launch from one prompt</span>
                </div>

                <h1>Build websites with AI</h1>

                <p>
                    Tell Nexus what you want to create. We will prepare your website,
                    CRM modules, lead flow, and preview.
                </p>
            </div>

            <div className="mgc-prompt-card">

                <input
                    type="text"
                    value={form.businessName || ""}
                    onChange={(e) => updateField("businessName", e.target.value)}
                    placeholder="Business name"
                    className="mgc-business-input"
                />

                <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe your business... e.g. Build a fitness studio website with modern equipment, trainers, memberships and trial session enquiries"
                    rows={5}
                />

                <div className="mgc-prompt-footer">
                    <div className="mgc-detected">
                        <span>Detected</span>
                        <strong>{selectedPreview}</strong>
                    </div>

                    <button
                        type="button"
                        className="mgc-send-btn"
                        disabled={!canSubmit}
                        onClick={() => applyPrompt(prompt)}
                        aria-label="Start AI builder"
                    >
                        <ArrowUp size={20} />
                    </button>
                </div>
            </div>

            <div className="mgc-quick-section">
                <div className="mgc-section-title">
                    <span>Quick starts</span>
                    <strong>Choose a business type</strong>
                </div>

                <div className="mgc-chip-row">
                    {quickStarts.map((item) => {
                        const Icon = item.icon;

                        return (
                            <button
                                type="button"
                                key={item.key}
                                className="mgc-chip"
                                onClick={() => applyQuickStart(item)}
                            >
                                <Icon size={15} />
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="mgc-proof-row">
                <span>Website</span>
                <span>CRM</span>
                <span>Leads</span>
                <span>Publish</span>
            </div>
        </section>
    );
}